import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackMapLaunch, trackReportDownload, trackPricingView, trackBlogArticleView, trackBlogInteraction } from '../utils/analytics';
import LogoNav from '../components/Navigation/LogoNav';
import './Blog.css';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  date: string;
  readTime: string;
  category: string;
  tags: string[];
  imageUrl?: string;
}

const Blog: React.FC = () => {
  const navigate = useNavigate();
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);

  // Sample blog posts - in a real app, this would come from a CMS or API
  const blogPosts: BlogPost[] = [
    {
      id: '1',
      title: 'Understanding Toronto Zoning: A Complete Guide for Real Estate Professionals',
      excerpt: 'Navigate Toronto\'s complex zoning bylaws with confidence. Learn how to identify development potential, understand setback requirements, and maximize property value.',
      content: `
        <h2>Introduction to Toronto Zoning</h2>
        <p>Toronto's zoning bylaws can seem overwhelming, but understanding them is crucial for any real estate professional working in the city. With over 40 different residential zones and numerous commercial and mixed-use designations, Toronto's zoning system is one of the most complex in North America.</p>
        
        <h3>Key Zoning Categories</h3>
        <p><strong>Residential Zones:</strong> From R1 (single-family detached) to R4 (apartment buildings), each zone has specific rules about density, height, and setbacks.</p>
        
        <p><strong>Commercial Zones:</strong> C1 through C4 zones allow different types of commercial activities, with varying restrictions on building size and use.</p>
        
        <p><strong>Mixed-Use Zones:</strong> These zones combine residential and commercial uses, offering flexibility for developers.</p>
        
        <h3>Understanding Development Potential</h3>
        <p>One of the most important aspects of zoning analysis is determining a property's development potential. This includes:</p>
        <ul>
          <li>Maximum building height and density</li>
          <li>Required setbacks from property lines</li>
          <li>Parking requirements</li>
          <li>Heritage considerations</li>
        </ul>
        
        <h3>Common Zoning Challenges</h3>
        <p>Real estate professionals often encounter several challenges when dealing with Toronto zoning:</p>
        <ul>
          <li><strong>Zoning Exceptions:</strong> Many properties have site-specific exceptions that modify standard zoning rules</li>
          <li><strong>Heritage Designations:</strong> Properties in heritage districts have additional restrictions</li>
          <li><strong>Overlay Zones:</strong> Special policy areas that add extra requirements</li>
          <li><strong>Recent Changes:</strong> Zoning bylaws are frequently updated, making it hard to stay current</li>
        </ul>
        
        <h3>How ZoningPal Helps</h3>
        <p>ZoningPal's AI-powered analysis eliminates the guesswork by instantly analyzing all relevant zoning bylaws, heritage considerations, and overlay requirements for any Toronto property. Our comprehensive reports provide:</p>
        <ul>
          <li>Clear zoning classification and permitted uses</li>
          <li>Development potential analysis</li>
          <li>Setback and height requirements</li>
          <li>Parking requirements</li>
          <li>Heritage considerations</li>
          <li>Overlay zone impacts</li>
        </ul>
        
        <h3>Best Practices for Zoning Analysis</h3>
        <p>When analyzing a property's zoning potential:</p>
        <ol>
          <li>Always check for zoning exceptions and site-specific provisions</li>
          <li>Consider heritage designations and their impact on development</li>
          <li>Review overlay zones and special policy areas</li>
          <li>Verify current zoning status - bylaws change frequently</li>
          <li>Consult with planning professionals for complex cases</li>
        </ol>
        
        <h3>Conclusion</h3>
        <p>Understanding Toronto zoning is essential for real estate success. While the system is complex, tools like ZoningPal make it accessible to professionals at all levels. By combining traditional research methods with modern AI analysis, you can confidently navigate Toronto's zoning landscape and identify opportunities others might miss.</p>
      `,
      author: 'ZoningPal Team',
      date: '2024-10-26',
      readTime: '8 min read',
      category: 'Zoning Guide',
      tags: ['Toronto Zoning', 'Real Estate', 'Development', 'Zoning Analysis'],
      imageUrl: '/toronto-zoning-guide.png'
    },
    {
      id: '2',
      title: 'The Future of Property Analysis: How AI is Transforming Real Estate Due Diligence',
      excerpt: 'Discover how artificial intelligence is revolutionizing property analysis, making complex zoning research faster, more accurate, and accessible to all real estate professionals.',
      content: `
        <h2>The Traditional Challenge</h2>
        <p>For decades, real estate professionals have relied on manual research to understand property potential. This process typically involves:</p>
        <ul>
          <li>Hours of digging through municipal websites</li>
          <li>Reading hundreds of pages of zoning bylaws</li>
          <li>Cross-referencing multiple data sources</li>
          <li>Interpreting complex legal language</li>
        </ul>
        
        <h3>Enter Artificial Intelligence</h3>
        <p>AI is transforming this landscape by automating the analysis process. Modern AI systems can:</p>
        <ul>
          <li>Process thousands of pages of bylaws in seconds</li>
          <li>Identify relevant regulations automatically</li>
          <li>Cross-reference multiple data sources</li>
          <li>Present findings in clear, actionable formats</li>
        </ul>
        
        <h3>Benefits for Real Estate Professionals</h3>
        <p>The advantages of AI-powered property analysis are significant:</p>
        <ul>
          <li><strong>Speed:</strong> Analysis that once took hours now takes minutes</li>
          <li><strong>Accuracy:</strong> AI doesn't miss details or make human errors</li>
          <li><strong>Consistency:</strong> Every analysis follows the same thorough process</li>
          <li><strong>Accessibility:</strong> Complex analysis becomes available to all professionals</li>
        </ul>
        
        <h3>ZoningPal's AI Approach</h3>
        <p>ZoningPal leverages advanced AI to provide comprehensive property analysis:</p>
        <ul>
          <li>Natural language processing to understand zoning bylaws</li>
          <li>Machine learning to identify patterns and exceptions</li>
          <li>Geographic analysis to determine overlay impacts</li>
          <li>Automated report generation with clear recommendations</li>
        </ul>
        
        <h3>The Future is Here</h3>
        <p>AI-powered property analysis isn't a future concept - it's available today. Early adopters are already seeing significant advantages in their due diligence processes.</p>
      `,
      author: 'ZoningPal Team',
      date: '2024-10-25',
      readTime: '6 min read',
      category: 'Technology',
      tags: ['AI', 'Property Analysis', 'Technology', 'Real Estate Innovation'],
      imageUrl: '/ai-property-analysis.png'
    },
    {
      id: '3',
      title: 'Toronto Development Trends: What the Data Tells Us About the City\'s Growth',
      excerpt: 'Analyze Toronto\'s development patterns and understand how zoning changes are shaping the city\'s future growth and real estate opportunities.',
      content: `
        <h2>Toronto's Development Landscape</h2>
        <p>Toronto is experiencing unprecedented growth, with development patterns that reflect the city's evolving needs and priorities. Understanding these trends is crucial for real estate professionals.</p>
        
        <h3>Key Development Trends</h3>
        <p>Recent analysis reveals several important trends:</p>
        <ul>
          <li><strong>Mixed-Use Development:</strong> Increasing preference for combining residential and commercial uses</li>
          <li><strong>Transit-Oriented Development:</strong> Focus on development near transit corridors</li>
          <li><strong>Heritage Integration:</strong> Balancing preservation with development needs</li>
          <li><strong>Density Optimization:</strong> Maximizing land use efficiency</li>
        </ul>
        
        <h3>Zoning Policy Changes</h3>
        <p>Toronto's zoning policies are evolving to support these trends:</p>
        <ul>
          <li>New mixed-use zones in key areas</li>
          <li>Height and density bonuses for transit proximity</li>
          <li>Streamlined approval processes for certain developments</li>
          <li>Enhanced heritage protection measures</li>
        </ul>
        
        <h3>Opportunities for Professionals</h3>
        <p>These trends create opportunities for:</p>
        <ul>
          <li>Identifying underutilized properties with development potential</li>
          <li>Understanding emerging market areas</li>
          <li>Advising clients on zoning strategy</li>
          <li>Staying ahead of policy changes</li>
        </ul>
        
        <h3>Using Data for Analysis</h3>
        <p>Modern tools like ZoningPal help professionals stay current with these trends by providing instant access to comprehensive zoning data and analysis.</p>
      `,
      author: 'ZoningPal Team',
      date: '2024-10-24',
      readTime: '5 min read',
      category: 'Market Analysis',
      tags: ['Toronto Development', 'Market Trends', 'Zoning Policy', 'Real Estate'],
      imageUrl: '/toronto-development-trends.png'
    },
    {
      id: '4',
      title: 'Zoning Analysis Best Practices: A Professional Guide',
      excerpt: 'Master the art of zoning analysis with proven strategies and techniques used by Toronto\'s top real estate professionals.',
      content: `
        <h2>Mastering Zoning Analysis</h2>
        <p>Effective zoning analysis requires a systematic approach and deep understanding of Toronto's complex regulatory environment.</p>
        
        <h3>Step-by-Step Analysis Process</h3>
        <p>Follow this proven methodology for comprehensive zoning analysis:</p>
        <ol>
          <li><strong>Property Identification:</strong> Verify exact property boundaries and legal description</li>
          <li><strong>Zoning Classification:</strong> Determine current zoning designation and permitted uses</li>
          <li><strong>Exception Review:</strong> Check for site-specific zoning exceptions</li>
          <li><strong>Heritage Assessment:</strong> Identify heritage designations and restrictions</li>
          <li><strong>Overlay Analysis:</strong> Review special policy areas and overlay zones</li>
          <li><strong>Development Potential:</strong> Calculate maximum density, height, and setbacks</li>
        </ol>
        
        <h3>Common Pitfalls to Avoid</h3>
        <p>Learn from common mistakes in zoning analysis:</p>
        <ul>
          <li>Overlooking site-specific exceptions</li>
          <li>Missing heritage designations</li>
          <li>Ignoring overlay zone requirements</li>
          <li>Using outdated zoning information</li>
          <li>Failing to consider parking requirements</li>
        </ul>
        
        <h3>Professional Tips</h3>
        <p>Insider knowledge from experienced professionals:</p>
        <ul>
          <li>Always verify current zoning status - bylaws change frequently</li>
          <li>Consider the impact of proposed policy changes</li>
          <li>Build relationships with planning department staff</li>
          <li>Use technology to streamline the analysis process</li>
          <li>Document all findings thoroughly</li>
        </ul>
      `,
      author: 'ZoningPal Team',
      date: '2024-10-23',
      readTime: '7 min read',
      category: 'Zoning Guide',
      tags: ['Zoning Analysis', 'Best Practices', 'Professional Guide', 'Toronto Real Estate'],
      imageUrl: '/category-zoning.png'
    },
    {
      id: '5',
      title: 'Technology Trends in Real Estate: What\'s Next?',
      excerpt: 'Explore the latest technological innovations transforming the real estate industry and how they\'re changing the way professionals work.',
      content: `
        <h2>The Digital Transformation of Real Estate</h2>
        <p>Technology is revolutionizing every aspect of real estate, from property analysis to client interactions.</p>
        
        <h3>Emerging Technologies</h3>
        <p>Key technologies reshaping the industry:</p>
        <ul>
          <li><strong>Artificial Intelligence:</strong> Automated property analysis and market predictions</li>
          <li><strong>Virtual Reality:</strong> Immersive property tours and development visualization</li>
          <li><strong>Blockchain:</strong> Secure property transactions and smart contracts</li>
          <li><strong>IoT Sensors:</strong> Smart building management and energy optimization</li>
          <li><strong>Big Data Analytics:</strong> Market trend analysis and investment insights</li>
        </ul>
        
        <h3>Impact on Real Estate Professionals</h3>
        <p>How technology is changing the profession:</p>
        <ul>
          <li>Faster, more accurate property analysis</li>
          <li>Enhanced client communication and service</li>
          <li>Streamlined transaction processes</li>
          <li>Better market insights and predictions</li>
          <li>Improved efficiency and productivity</li>
        </ul>
        
        <h3>Future Outlook</h3>
        <p>The future of real estate technology promises even greater innovation and efficiency.</p>
      `,
      author: 'ZoningPal Team',
      date: '2024-10-22',
      readTime: '6 min read',
      category: 'Technology',
      tags: ['Technology', 'Real Estate Innovation', 'AI', 'Digital Transformation'],
      imageUrl: '/category-technology.png'
    },
    {
      id: '6',
      title: 'Toronto Real Estate Market Analysis: Q4 2024 Insights',
      excerpt: 'Comprehensive analysis of Toronto\'s real estate market trends, pricing patterns, and investment opportunities for Q4 2024.',
      content: `
        <h2>Market Overview</h2>
        <p>Toronto's real estate market continues to show resilience and growth, with several key trends emerging in Q4 2024.</p>
        
        <h3>Key Market Indicators</h3>
        <p>Current market metrics and trends:</p>
        <ul>
          <li><strong>Average Home Prices:</strong> Continued growth in most neighborhoods</li>
          <li><strong>Inventory Levels:</strong> Balanced supply and demand dynamics</li>
          <li><strong>Days on Market:</strong> Stable selling times across property types</li>
          <li><strong>Interest Rates:</strong> Impact on buyer behavior and affordability</li>
        </ul>
        
        <h3>Neighborhood Analysis</h3>
        <p>Performance across key Toronto neighborhoods:</p>
        <ul>
          <li><strong>Downtown Core:</strong> Strong condo market with premium pricing</li>
          <li><strong>East End:</strong> Emerging opportunities in revitalizing areas</li>
          <li><strong>West End:</strong> Stable family home market</li>
          <li><strong>North York:</strong> Mixed-use development opportunities</li>
        </ul>
        
        <h3>Investment Opportunities</h3>
        <p>Areas showing strong potential for investors:</p>
        <ul>
          <li>Transit-oriented development sites</li>
          <li>Mixed-use zoning opportunities</li>
          <li>Heritage property redevelopment</li>
          <li>Commercial-to-residential conversions</li>
        </ul>
        
        <h3>Market Predictions</h3>
        <p>Looking ahead to 2025, we expect continued growth in strategic areas with focus on sustainable development.</p>
      `,
      author: 'ZoningPal Team',
      date: '2024-10-21',
      readTime: '8 min read',
      category: 'Market Analysis',
      tags: ['Market Analysis', 'Toronto Real Estate', 'Investment', 'Market Trends'],
      imageUrl: '/category-market.png'
    }
  ];

  const handleReadMore = (post: BlogPost) => {
    // Track blog article view
    trackBlogArticleView(post.title, post.category);
    // Track blog interaction
    trackBlogInteraction('read_more_clicked', post.title);
    setSelectedPost(post);
  };

  const handleBackToList = () => {
    // Track blog interaction
    trackBlogInteraction('back_to_list');
    setSelectedPost(null);
  };

  const handleCTAClick = (action: string) => {
    switch (action) {
      case 'map':
        trackMapLaunch('blog');
        navigate('/map');
        break;
      case 'sample':
        trackReportDownload('blog');
        const link = document.createElement('a');
        link.href = '/zoning-analysis-sample.pdf';
        link.download = 'ZoningPal-Sample-Report.pdf';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        break;
      case 'pricing':
        trackPricingView('blog');
        navigate('/pricing');
        break;
    }
  };

  if (selectedPost) {
    return (
      <div className="blog-container">
        <LogoNav />
        <div className="blog-header">
          <button className="back-button" onClick={handleBackToList}>
            ← Back to Blog
          </button>
          <div className="blog-meta">
            <span className="category">{selectedPost.category}</span>
            <span className="date">{selectedPost.date}</span>
            <span className="read-time">{selectedPost.readTime}</span>
          </div>
        </div>
        
        <article className="blog-post">
          <h1 className="post-title">{selectedPost.title}</h1>
          <div className="post-meta">
            <span className="author">By {selectedPost.author}</span>
          </div>
          
          {selectedPost.imageUrl && (
            <div className="post-image">
              <img src={selectedPost.imageUrl} alt={selectedPost.title} />
            </div>
          )}
          
          <div 
            className="post-content"
            dangerouslySetInnerHTML={{ __html: selectedPost.content }}
          />
          
          <div className="post-tags">
            {selectedPost.tags.map((tag, index) => (
              <span key={index} className="tag">#{tag}</span>
            ))}
          </div>
        </article>
        
        <div className="blog-cta">
          <h3>Ready to Analyze Your Property?</h3>
          <p>Put this knowledge into practice with ZoningPal's AI-powered analysis.</p>
          <div className="cta-buttons">
            <button 
              className="btn-primary" 
              onClick={() => handleCTAClick('map')}
            >
              Launch Interactive Map
            </button>
            <button 
              className="btn-secondary" 
              onClick={() => handleCTAClick('sample')}
            >
              Download Sample Report
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-container">
      <LogoNav />
      <div className="blog-header">
        <h1>ZoningPal Blog</h1>
        <p>Insights, guides, and analysis for Toronto real estate professionals</p>
      </div>
      
      <div className="blog-grid">
        {blogPosts.map((post) => (
          <article key={post.id} className="blog-card">
            {post.imageUrl && (
              <div className="card-image">
                <img src={post.imageUrl} alt={post.title} />
              </div>
            )}
            
            <div className="card-content">
              <div className="card-meta">
                <span className="category">{post.category}</span>
                <span className="date">{post.date}</span>
                <span className="read-time">{post.readTime}</span>
              </div>
              
              <h2 className="card-title">{post.title}</h2>
              <p className="card-excerpt">{post.excerpt}</p>
              
              <div className="card-tags">
                {post.tags.slice(0, 3).map((tag, index) => (
                  <span key={index} className="tag">#{tag}</span>
                ))}
              </div>
              
              <button 
                className="read-more-btn"
                onClick={() => handleReadMore(post)}
              >
                Read More
              </button>
            </div>
          </article>
        ))}
      </div>
      
      <div className="blog-cta">
        <h3>Stay Updated</h3>
        <p>Get the latest insights on Toronto zoning and real estate trends.</p>
        <div className="cta-buttons">
          <button 
            className="btn-primary" 
            onClick={() => handleCTAClick('map')}
          >
            Try ZoningPal
          </button>
          <button 
            className="btn-secondary" 
            onClick={() => handleCTAClick('pricing')}
          >
            View Pricing
          </button>
        </div>
      </div>
    </div>
  );
};

export default Blog;
